/**
 * Pure pathway due-evaluation helpers mirrored from the patient app's
 * pathwayService.js. Care uses these read-only for Patient To-Do's — never
 * call autoReplaceSweep / close orphan progress from here.
 */

function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseTime(t) {
  const [h, m] = String(t || '09:00').split(':').map(Number);
  return { hours: Number.isFinite(h) ? h : 9, minutes: Number.isFinite(m) ? m : 0 };
}

export function isReadingAssignment(a) {
  return (a?.triggerSource || 'manual') === 'reading';
}

function eligibleForPatientSideRun(a) {
  if (!a) return false;
  return !isReadingAssignment(a);
}

export function isCompletedOneShot(a) {
  if (!a || a.completedByPatient !== true) return false;
  const kind = a?.schedule?.kind;
  return (a.triggerSource || 'manual') === 'manual' && (kind === 'now' || kind === 'futureAt');
}

/**
 * Whether an existing assignment prevents a new one for the same pathway:
 * active ones do, except one-time manual assignments the patient already finished.
 */
export function blocksNewAssignment(a) {
  return !!a && a.active === true && !isCompletedOneShot(a);
}

/**
 * Most recent fire time at-or-before `now`, or null if the assignment has not
 * fired yet (including fires that predate assignedAt).
 */
export function mostRecentRecurringFireTime(assignment, now) {
  const occurrences = assignment?.schedule?.recurring?.occurrences || [];
  if (!Array.isArray(occurrences) || occurrences.length === 0) return null;
  let best = null;
  for (const o of occurrences) {
    const { hours, minutes } = parseTime(o.time);
    if (o.type === 'daily') {
      const d = new Date(now);
      d.setHours(hours, minutes, 0, 0);
      if (d > now) d.setDate(d.getDate() - 1);
      if (!best || d > best) best = d;
    } else if (o.type === 'weekly') {
      const target = Number(o.dayOfWeek);
      if (!Number.isInteger(target)) continue;
      const d = new Date(now);
      d.setHours(hours, minutes, 0, 0);
      let delta = (now.getDay() - target + 7) % 7;
      if (delta === 0 && d > now) delta = 7;
      d.setDate(d.getDate() - delta);
      if (!best || d > best) best = d;
    } else if (o.type === 'monthly') {
      const dom = Math.min(28, Math.max(1, Number(o.dayOfMonth) || 1));
      const d = new Date(now.getFullYear(), now.getMonth(), dom, hours, minutes, 0, 0);
      if (d > now) d.setMonth(d.getMonth() - 1);
      if (!best || d > best) best = d;
    }
  }
  const assignedAt = toDate(assignment?.assignedAt);
  if (best && assignedAt && best < assignedAt) return null;
  return best;
}

export function assignmentAlreadyCompleted(assignment, completedList, now) {
  const aid = assignment.id;
  const completedForAssignment = completedList.filter((p) => p.pathwayAssignmentId === aid);
  if (completedForAssignment.length === 0) return false;
  const kind = assignment?.schedule?.kind;
  if (kind === 'now' || kind === 'futureAt') return true;
  if (kind === 'recurring') {
    const lastFire = mostRecentRecurringFireTime(assignment, now || new Date());
    if (!lastFire) return false;
    return completedForAssignment.some((p) => {
      const t = toDate(p.completedAt);
      return t && t >= lastFire;
    });
  }
  return true;
}

export function isAssignmentDueNow(assignment, now = new Date()) {
  if (!assignment || !assignment.active) return false;
  if (!eligibleForPatientSideRun(assignment)) return false;
  const kind = assignment?.schedule?.kind;
  if (kind === 'now') return true;
  if (kind === 'futureAt') {
    const t = toDate(assignment.schedule.futureAt);
    return !!t && now >= t;
  }
  if (kind === 'recurring') {
    return !!mostRecentRecurringFireTime(assignment, now);
  }
  return false;
}

/**
 * Next *future* fire time for an assignment, or null.
 */
export function getNextDueDate(assignment, now = new Date()) {
  if (!assignment || isReadingAssignment(assignment)) return null;
  const kind = assignment?.schedule?.kind;

  if (kind === 'now') return null;
  if (kind === 'futureAt') {
    const t = toDate(assignment.schedule.futureAt);
    return t && t > now ? t : null;
  }
  if (kind !== 'recurring') return null;

  const occurrences = assignment?.schedule?.recurring?.occurrences || [];
  if (occurrences.length === 0) return null;

  let best = null;
  for (const o of occurrences) {
    const { hours, minutes } = parseTime(o.time);
    if (o.type === 'daily') {
      const d = new Date(now);
      d.setHours(hours, minutes, 0, 0);
      if (d <= now) d.setDate(d.getDate() + 1);
      if (!best || d < best) best = d;
    } else if (o.type === 'weekly') {
      const target = Number(o.dayOfWeek);
      if (!Number.isInteger(target)) continue;
      const d = new Date(now);
      d.setHours(hours, minutes, 0, 0);
      let delta = (target - now.getDay() + 7) % 7;
      if (delta === 0 && d <= now) delta = 7;
      d.setDate(d.getDate() + delta);
      if (!best || d < best) best = d;
    } else if (o.type === 'monthly') {
      const dom = Math.min(28, Math.max(1, Number(o.dayOfMonth) || 1));
      let d = new Date(now.getFullYear(), now.getMonth(), dom, hours, minutes, 0, 0);
      if (d <= now) d = new Date(now.getFullYear(), now.getMonth() + 1, dom, hours, minutes, 0, 0);
      if (!best || d < best) best = d;
    }
  }
  return best;
}

/**
 * Which active manual assignments currently count as Home "questions" todos.
 * Read-only mirror of patient getDueQuestionAssignmentsCount (no sweeps).
 */
export function getDueQuestionAssignments(assignments, allProgress, now = new Date()) {
  const active = (assignments || []).filter((a) => a && a.active === true);
  const incompleteList = (allProgress || []).filter((p) => !p.completedAt && !p.closedAt);
  const completedList = (allProgress || []).filter((p) => p.completedAt || p.closedAt);

  const byAssignmentId = new Map();
  incompleteList.forEach((p) => {
    const aid = p.pathwayAssignmentId || p.pathwayId;
    if (aid == null) return;
    if (!byAssignmentId.has(aid)) byAssignmentId.set(aid, []);
    byAssignmentId.get(aid).push(p);
  });

  const due = [];
  for (const a of active) {
    if (!eligibleForPatientSideRun(a)) continue;
    if (isCompletedOneShot(a)) continue;
    if (!isAssignmentDueNow(a, now)) continue;
    const existing = byAssignmentId.get(a.id) || byAssignmentId.get(a.pathwayId);
    if (existing && existing.length > 0) {
      due.push(a);
      continue;
    }
    if (assignmentAlreadyCompleted(a, completedList, now)) continue;
    due.push(a);
  }
  return due;
}

/**
 * Active manual recurring/futureAt assignments that are not currently due on Home.
 */
export function getScheduledNotOnHomeAssignments(assignments, dueAssignments, now = new Date()) {
  const dueIds = new Set((dueAssignments || []).map((a) => a.id));
  return (assignments || []).filter((a) => {
    if (!a || a.active !== true) return false;
    if (!eligibleForPatientSideRun(a)) return false;
    if (isCompletedOneShot(a)) return false;
    if (dueIds.has(a.id)) return false;
    const kind = a.schedule?.kind;
    if (kind !== 'recurring' && kind !== 'futureAt') return false;
    return !!getNextDueDate(a, now);
  });
}

export function formatShortNextDue(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${mm}/${dd} ${timeStr}`;
}
